import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useResponsive } from "../../hooks/useResponsive";
import { config } from "../../config/env";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setSelectedWallet } from "../../store/slices/uiSlice";
import UserAvatar from "../ui/UserAvatar";
import LogoImage from "../../assets/logo.png";

export const SvgIcon: React.FC<{ d: string; className?: string }> = ({
  d,
  className = "w-4 h-4",
}) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

export const NAV_ICONS: Record<string, string> = {
  dashboard:
    "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  chart:
    "M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z",
  deposit: "M19 14l-7 7m0 0l-7-7m7 7V3",
  withdraw: "M5 10l7-7m0 0l7 7m-7-7v18",
  history:
    "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  kyc: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  user: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  wallet:
    "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  shield:
    "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  settings:
    "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  logout:
    "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1",
  admin:
    "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  bell: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
  menu: "M4 6h16M4 12h16M4 18h16",
  closex: "M6 18L18 6M6 6l12 12",
  profile:
    "M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z",
  arrow_right: "M14 5l7 7m0 0l-7 7m7-7H3",
};

const USER_LINKS = [
  { to: "/dashboard", icon: "dashboard", label: "Dashboard" },
  { to: "/trade", icon: "chart", label: "Trade" },
  { to: "/deposit", icon: "deposit", label: "Deposit" },
  { to: "/withdraw", icon: "withdraw", label: "Withdraw" },
  { to: "/transactions", icon: "history", label: "History" },
  { to: "/kyc", icon: "kyc", label: "KYC" },
  { to: "/profile", icon: "profile", label: "Profile" },
];

const ADMIN_LINKS = [
  { to: "/admin", icon: "dashboard", label: "Dashboard" },
  { to: "/admin/users", icon: "user", label: "Users" },
  { to: "/admin/trades", icon: "chart", label: "Trades" },
  { to: "/admin/deposits", icon: "deposit", label: "Deposits" },
  { to: "/admin/withdrawals", icon: "withdraw", label: "Withdrawals" },
  { to: "/admin/kyc", icon: "shield", label: "KYC" },
  { to: "/admin/addresses", icon: "wallet", label: "Addresses" },
  { to: "/admin/settings", icon: "settings", label: "Settings" },
];

interface NavItemProps {
  to: string;
  icon: string;
  label: string;
  mobile?: boolean;
  onNavigate?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({
  to,
  icon,
  label,
  mobile,
  onNavigate,
}) => {
  const location = useLocation();
  const exact = to === "/dashboard" || to === "/admin";
  const isActive = exact
    ? location.pathname === to
    : location.pathname.startsWith(to);

  if (mobile) {
    return (
      <NavLink
        to={to}
        onClick={onNavigate}
        className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-1.5 transition-colors ${
          isActive ? "text-primary" : "text-gray-500"
        }`}
      >
        <SvgIcon
          d={NAV_ICONS[icon] || NAV_ICONS.dashboard}
          className="w-[18px] h-[18px]"
        />
        <span className="text-[8px] font-display font-bold tracking-wide leading-none">
          {label}
        </span>
      </NavLink>
    );
  }

  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl transition-all duration-150 ${
        isActive
          ? "bg-primary/10 text-primary border border-primary/20"
          : "text-gray-400 hover:text-white hover:bg-white/[0.04]"
      }`}
    >
      <SvgIcon
        d={NAV_ICONS[icon] || NAV_ICONS.dashboard}
        className="w-3.5 h-3.5 shrink-0"
      />
      <span className="text-[13px] font-display font-semibold">{label}</span>
      {isActive && (
        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
      )}
    </NavLink>
  );
};

// ── Logo — used in both sidebar and header ────────────────────────────────────
export const Logo: React.FC<{ sidebar?: boolean }> = ({ sidebar }) => {
  const [imgFailed, setImgFailed] = useState(false);

  // Sidebar variant: full padding + bottom border (unchanged from original)
  if (sidebar) {
    return (
      <div className="flex items-center gap-2.5 px-3.5 pt-4 pb-3 border-b border-gray-800/40 shrink-0">
        {imgFailed ? (
          <SvgIcon
            d={NAV_ICONS.chart}
            className="w-8 h-8 text-primary shrink-0"
          />
        ) : (
          <img
            src={LogoImage}
            alt={config.appName}
            className="w-8 h-8 object-contain shrink-0"
            onError={() => setImgFailed(true)}
          />
        )}
        <div className="flex flex-col leading-none min-w-0">
          <span className="font-display font-bold text-primary text-[13px] tracking-tight truncate">
            {config.appName}
          </span>
        </div>
      </div>
    );
  }

  // Header variant: no padding/border — sits flush inside the header bar
  return (
    <div className="flex items-center gap-2 shrink-0">
      {imgFailed ? (
        <SvgIcon
          d={NAV_ICONS.chart}
          className="w-7 h-7 text-primary shrink-0"
        />
      ) : (
        <img
          src={LogoImage}
          alt={config.appName}
          className="w-7 h-7 object-contain shrink-0"
          onError={() => setImgFailed(true)}
        />
      )}
      <span className="font-display font-bold text-primary text-[13px] tracking-tight">
        {config.appName}
      </span>
    </div>
  );
};

export const AccountSwitcher: React.FC<{ compact?: boolean }> = ({
  compact,
}) => {
  const dispatch = useAppDispatch();
  const mode = useAppSelector((s) => s.ui.selectedWallet);
  const toggle = (m: "real" | "demo") => {
    dispatch(setSelectedWallet(m));
    localStorage.setItem("accountMode", m);
  };

  if (compact) {
    return (
      <div className="flex items-center bg-gray-900 rounded-md border border-gray-800 p-0.5 gap-0.5 shrink-0">
        <button
          onClick={() => toggle("real")}
          className={`px-2 py-0.5 rounded text-[10px] font-display font-bold transition-all ${
            mode === "real"
              ? "bg-primary text-black"
              : "text-gray-400 hover:text-white"
          }`}
        >
          REAL
        </button>
        <button
          onClick={() => toggle("demo")}
          className={`px-2 py-0.5 rounded text-[10px] font-display font-bold transition-all ${
            mode === "demo"
              ? "bg-blue-500 text-white"
              : "text-gray-400 hover:text-white"
          }`}
        >
          DEMO
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 shrink-0">
      <p className="text-[9px] font-bold text-gray-600 uppercase tracking-widest px-0.5 mb-1.5">
        Account Mode
      </p>
      <div className="flex bg-gray-900/60 rounded-xl border border-gray-800/50 p-0.5 gap-0.5">
        <button
          onClick={() => toggle("real")}
          className={`flex-1 py-1.5 rounded-lg text-[11px] font-display font-bold transition-all ${
            mode === "real"
              ? "bg-primary text-black"
              : "text-gray-400 hover:text-white"
          }`}
        >
          💰 Real
        </button>
        <button
          onClick={() => toggle("demo")}
          className={`flex-1 py-1.5 rounded-lg text-[11px] font-display font-bold transition-all ${
            mode === "demo"
              ? "bg-blue-500 text-white"
              : "text-gray-400 hover:text-white"
          }`}
        >
          🎮 Demo
        </button>
      </div>
    </div>
  );
};

const PanelToggle: React.FC<{
  isAdminArea: boolean;
  onNavigate?: () => void;
}> = ({ isAdminArea, onNavigate }) => {
  const navigate = useNavigate();
  const go = (p: string) => {
    navigate(p);
    onNavigate?.();
  };
  return (
    <div className="px-3 pt-2 pb-1 shrink-0">
      <div className="flex bg-gray-900/60 rounded-xl border border-gray-800/50 p-0.5 gap-0.5">
        <button
          onClick={() => go("/dashboard")}
          className={`flex-1 py-1.5 rounded-lg text-[10px] font-display font-bold transition-all flex items-center justify-center gap-1 ${
            !isAdminArea
              ? "bg-primary text-black"
              : "text-gray-400 hover:text-white"
          }`}
        >
          <SvgIcon d={NAV_ICONS.user} className="w-3 h-3" /> User
        </button>
        <button
          onClick={() => go("/admin")}
          className={`flex-1 py-1.5 rounded-lg text-[10px] font-display font-bold transition-all flex items-center justify-center gap-1 ${
            isAdminArea
              ? "bg-yellow-500 text-black"
              : "text-gray-400 hover:text-white"
          }`}
        >
          <SvgIcon d={NAV_ICONS.admin} className="w-3 h-3" /> Admin
        </button>
      </div>
    </div>
  );
};

const NavContent: React.FC<{
  isAdminArea: boolean;
  isAdmin: boolean;
  user: any;
  onNavigate?: () => void;
  onLogout: () => void;
}> = ({ isAdminArea, isAdmin, user, onNavigate, onLogout }) => {
  const navigate = useNavigate();
  const links = isAdminArea ? ADMIN_LINKS : USER_LINKS;
  return (
    <div className="flex flex-col flex-1 min-h-0">
      {isAdmin && (
        <PanelToggle isAdminArea={isAdminArea} onNavigate={onNavigate} />
      )}
      {!isAdminArea && <AccountSwitcher />}
      <nav className="flex-1 px-2.5 py-1 flex flex-col gap-0.5 overflow-y-auto">
        {links.map((l) => (
          <NavItem key={l.to} {...l} onNavigate={onNavigate} />
        ))}
      </nav>
      <div className="px-2.5 pb-4 pt-2 border-t border-gray-800/40 shrink-0">
        <div
          className="flex items-center gap-2 px-2 py-2 mb-1 rounded-xl hover:bg-white/[0.04] cursor-pointer transition-all"
          onClick={() => {
            navigate("/profile");
            onNavigate?.();
          }}
        >
          <UserAvatar user={user} size={28} />
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-white truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-[10px] text-gray-500 truncate">
              @{user?.username}
            </p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center gap-2 px-2 py-1.5 rounded-xl text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all w-full text-[12px] font-display font-semibold"
        >
          <SvgIcon d={NAV_ICONS.logout} className="w-3.5 h-3.5" /> Sign Out
        </button>
      </div>
    </div>
  );
};

// ── Drawer ────────────────────────────────────────────────────────────────────
const Drawer: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const { user, isAdmin, logout } = useAuth();
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith("/admin");

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/70 z-40 transition-opacity duration-200 ${
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 left-0 h-full w-[240px] bg-[#080808] border-r border-gray-800/50 flex flex-col z-50 transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between pr-3 shrink-0">
          <Logo sidebar />
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 transition-all"
          >
            <SvgIcon d={NAV_ICONS.closex} className="w-4 h-4" />
          </button>
        </div>
        <NavContent
          isAdminArea={isAdminArea}
          isAdmin={isAdmin}
          user={user}
          onNavigate={onClose}
          onLogout={() => {
            onClose();
            logout();
          }}
        />
      </div>
    </>
  );
};

// ── Shared balance hook ───────────────────────────────────────────────────────
const useFormattedBalance = () => {
  const { user } = useAuth();
  const mode = useAppSelector((s) => s.ui.selectedWallet);
  const wallet = mode === "real" ? user?.real_wallet : user?.demo_wallet;
  const balance = wallet
    ? parseFloat(wallet.balance as string).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "$0.00";
  return { balance, mode };
};

// ── Balance Display (desktop header) ─────────────────────────────────────────
const BalanceDisplay: React.FC = () => {
  const { balance, mode } = useFormattedBalance();
  return (
    <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-gray-900/60 border border-gray-800/50 rounded-lg shrink-0">
      <span
        className={`w-1.5 h-1.5 rounded-full shrink-0 ${
          mode === "real" ? "bg-primary" : "bg-blue-500"
        }`}
      />
      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
        {mode}
      </span>
      <span className="text-[13px] font-display font-bold text-white">
        {balance}
      </span>
    </div>
  );
};

// ── Mobile Bottom Nav ─────────────────────────────────────────────────────────
const MobileBottomNav: React.FC<{ onMenuOpen: () => void }> = ({
  onMenuOpen,
}) => {
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith("/admin");
  const quickLinks = isAdminArea
    ? ADMIN_LINKS.slice(0, 4)
    : USER_LINKS.slice(0, 4);
  const { balance, mode } = useFormattedBalance();

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-[#090909] border-t border-gray-800/60 z-40 flex flex-col"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 4px)" }}
    >
      {/* Balance strip — only shown outside admin area */}
      {!isAdminArea && (
        <div className="flex items-center justify-center pt-1.5 pb-0.5">
          <div
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full border ${
              mode === "real"
                ? "bg-primary/5 border-primary/20"
                : "bg-blue-500/5 border-blue-500/20"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                mode === "real" ? "bg-primary" : "bg-blue-500"
              }`}
            />
            <span
              className={`text-[9px] font-bold uppercase tracking-wider ${
                mode === "real" ? "text-primary/60" : "text-blue-400/60"
              }`}
            >
              {mode}
            </span>
            <span className="text-[12px] font-display font-bold text-white">
              {balance}
            </span>
          </div>
        </div>
      )}

      {/* Nav icons row */}
      <div className="flex items-stretch">
        {quickLinks.map((l) => (
          <NavItem key={l.to} {...l} mobile />
        ))}
        <button
          onClick={onMenuOpen}
          className="flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-1.5 text-gray-500 active:text-gray-300 transition-colors"
        >
          <SvgIcon d={NAV_ICONS.menu} className="w-[18px] h-[18px]" />
          <span className="text-[8px] font-display font-bold tracking-wide leading-none">
            More
          </span>
        </button>
      </div>
    </nav>
  );
};

// ── Header ────────────────────────────────────────────────────────────────────
export const Header: React.FC<{ onMenuOpen?: () => void }> = ({
  onMenuOpen,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith("/admin");

  return (
    <header className="h-12 flex items-center justify-between px-3 md:px-4 border-b border-gray-800/40 bg-[#080808]/95 backdrop-blur-sm sticky top-0 z-30 shrink-0 gap-2">
      {/* Left — menu button + logo + app name */}
      <div className="flex items-center gap-2.5 min-w-0">
        <button
          onClick={onMenuOpen}
          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 shrink-0 transition-all"
        >
          <SvgIcon d={NAV_ICONS.menu} className="w-4 h-4" />
        </button>
        <Logo />
      </div>

      {/* Right — balance + controls + profile */}
      <div className="flex items-center gap-1.5 shrink-0">
        {/* Admin message bell */}
        {user?.admin_message && (
          <div className="hidden lg:flex items-center gap-1.5 px-2 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-lg max-w-[180px]">
            <SvgIcon
              d={NAV_ICONS.bell}
              className="w-3 h-3 text-yellow-400 shrink-0"
            />
            <span className="text-yellow-300 text-[10px] truncate">
              {user.admin_message}
            </span>
          </div>
        )}

        {/* Balance pill — desktop only (mobile sees it in bottom nav) */}
        {!isAdminArea && <BalanceDisplay />}

        {/* Wallet switcher */}
        {!isAdminArea && <AccountSwitcher compact />}

        {/* Profile avatar */}
        {user && (
          <div
            className="cursor-pointer hover:ring-2 hover:ring-primary/40 rounded-full transition-all shrink-0"
            onClick={() => navigate("/profile")}
            title="Profile"
          >
            <UserAvatar user={user} size={28} />
          </div>
        )}
      </div>
    </header>
  );
};

// ── Root Layout ───────────────────────────────────────────────────────────────
export const Layout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isMobile } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-[#060606]">
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="flex flex-col flex-1 min-w-0 min-h-screen">
        <Header onMenuOpen={() => setDrawerOpen(true)} />
        <main
          className={`flex-1 w-full overflow-x-hidden ${isMobile ? "pb-24" : ""}`}
        >
          {children}
        </main>
      </div>
      {isMobile && <MobileBottomNav onMenuOpen={() => setDrawerOpen(true)} />}
    </div>
  );
};
